/**
 * The employee's photo, as a data URL pdfmake can embed.
 *
 * The Lambda read the S3 object directly and base64'd it. In the browser the
 * bytes have to come through the signed files domain, and pdfmake cannot take a
 * URL - only a data URL - so the object URL is read back out again.
 *
 * The mime type matters: `requestFile` labels the blob image/png unless told
 * otherwise, and pdfmake trusts that label, so a JPEG announced as PNG fails to
 * parse. Everything here degrades to null, because a missing photo prints an
 * empty cell rather than failing the report.
 */
export const loadResourcePictureDataUrl = async (resources, requestFile) => {
  if (typeof requestFile !== 'function') {
    return null;
  }

  const withPicture = resources.find((resource) => resource.picture?.key);

  if (!withPicture) {
    return null;
  }

  const { key, file } = withPicture.picture;

  // The files domain flattens everything below the top folder into one segment.
  const folder = key.split('/')[0];
  const rest = key.substr(key.indexOf('/') + 1).replace(/\//g, '_');

  try {
    const objectUrl = await requestFile(`${folder}/${rest}`, file?.mimeType);

    if (!objectUrl) {
      return null;
    }

    const blob = await fetch(objectUrl).then((response) => response.blob());

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};
